import { getUserHasValidOrder, getUserIsBuy } from 'src/@api/login'
import { shouldEnterExpiredPurchasePage } from 'src/utils/subscriptionStatus'

/**
 * 进站入口判定。
 * 优先级固定（后端状态优先，本地推算仅作降级）：
 * 1. isBuy === false                    → 购买
 * 2. isBuy && shouldShowExpiredPage     → 过期超 30 天页
 * 3. 其余                               → 沿用设备 / 未绑单 / 绑定向导流程
 */
export const USER_ENTRY = Object.freeze({
  BUY: 'buy',
  EXPIRED: 'expired',
  CONTINUE: 'continue'
})

const readBoolean = value => (typeof value === 'boolean' ? value : null)

let flagsCache = null
let flagsLoading = null
let flagsGeneration = 0

/**
 * 读取 isBuy / shouldShowExpiredPage。
 * - isBuy：是否有过付费（订单或设备记录）
 * - shouldShowExpiredPage：由 /tsafely-order/valid 的 valid 取反（最新一笔已支付订单已过期超 30 天）
 * 任一请求失败时对应字段为 null，调用方回退到本地推算，避免把老用户误踢去购买页。
 * @returns {Promise<{ isBuy: boolean|null, shouldShowExpiredPage: boolean|null }>}
 */
export const loadUserEntryFlags = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh && flagsCache) return flagsCache
  if (flagsLoading) return flagsLoading

  const generation = flagsGeneration
  const request = Promise.all([
    getUserIsBuy().catch(() => null),
    getUserHasValidOrder().catch(() => null)
  ])
    .then(([buyRes, validRes]) => {
      const hasValidOrder = readBoolean(validRes?.data?.valid)
      const flags = {
        isBuy: readBoolean(buyRes?.data?.isBuy),
        shouldShowExpiredPage: hasValidOrder === null ? null : !hasValidOrder
      }
      if (generation === flagsGeneration) flagsCache = flags

      return flags
    })
    .finally(() => {
      if (flagsLoading === request) flagsLoading = null
    })

  flagsLoading = request

  return request
}

export const readUserEntryFlags = () => flagsCache

export const clearUserEntryFlags = () => {
  flagsGeneration += 1
  flagsCache = null
  flagsLoading = null
}

/**
 * 按固定优先级给出入口结论；后端状态拿不到时才用 devices / 未绑单本地推算。
 * @param {Object} params
 * @param {{ isBuy: boolean|null, shouldShowExpiredPage: boolean|null }} [params.flags]
 * @param {Array} [params.devices]
 * @param {Array} [params.validUnboundOrders]
 * @returns {'buy'|'expired'|'continue'}
 */
export const resolveUserEntry = ({ flags, devices = [], validUnboundOrders = [] } = {}) => {
  const isBuy = flags?.isBuy ?? null
  const shouldShowExpiredPage = flags?.shouldShowExpiredPage ?? null

  if (isBuy === false) return USER_ENTRY.BUY
  if (isBuy === true && shouldShowExpiredPage === true) return USER_ENTRY.EXPIRED

  // 降级：后端状态缺失时（例如接口未上线/请求失败）沿用旧的本地推算
  if (isBuy === null || shouldShowExpiredPage === null) {
    if (shouldEnterExpiredPurchasePage({ devices, validUnboundOrders })) return USER_ENTRY.EXPIRED
  }

  return USER_ENTRY.CONTINUE
}
